import React, { createContext, FC, useContext, useEffect, useState } from 'react'
import { Country } from '../domain/country.type'
import ApiContext from './ApiContext'
import { CountryHelper } from '../utils/countryHelper'

interface CountryContextI {
  countries: Country[]
  activeCountries: Country[]
  loading: boolean
  featureEnabled: boolean
  refreshCountries: () => void
}

const CountryContext = createContext<CountryContextI>({
  countries: [],
  activeCountries: [],
  loading: true,
  featureEnabled: true,
  refreshCountries: () => {},
})

export { CountryContext }

/**
 * Country Provider with automatic fallback to legacy enum
 * - If feature is enabled: uses dynamic countries from API
 * - If feature is disabled: falls back to hardcoded CountryEnum
 * - If API fails: gracefully falls back to ensure no UI crash
 */
export const CountryProvider: FC = ({ children }) => {
  const { getActiveCountries, isCountryManagementEnabled } = useContext(ApiContext)
  const [countries, setCountries] = useState<Country[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [featureEnabled, setFeatureEnabled] = useState<boolean>(true)

  const loadCountries = async () => {
    setLoading(true)
    try {
      // Check if feature is enabled
      const featureFlagResponse = await isCountryManagementEnabled()
      setFeatureEnabled(featureFlagResponse.enabled)

      if (featureFlagResponse.enabled) {
        // Use dynamic countries from API
        const apiCountries = await getActiveCountries()
        setCountries(apiCountries)
        console.info('Country management feature enabled, loaded', apiCountries.length, 'countries from API')
      } else {
        // Fallback to legacy hardcoded enum
        console.info('Country management feature disabled, using legacy enum')
        const legacyCountries = CountryHelper.enumToCountries()
        setCountries(legacyCountries)
      }
    } catch (error) {
      console.error('Failed to load countries, falling back to legacy enum', error)
      // Graceful fallback to ensure no UI crash
      const legacyCountries = CountryHelper.enumToCountries()
      setCountries(legacyCountries)
      setFeatureEnabled(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCountries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue: CountryContextI = {
    countries,
    activeCountries: countries.filter(c => c.active),
    loading,
    featureEnabled,
    refreshCountries: loadCountries,
  }

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  )
}

export default CountryContext
